import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Grid,
  Chip,
  Stack,
  Snackbar,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  OutlinedInput,
  Checkbox,
  ListItemText
} from '@mui/material';
import {
  Home as HomeIcon,
  ArrowBack as BackIcon,
  Person as UserIcon,
  CalendarToday as DateIcon,
  Check as ApproveIcon,
  Close as RejectIcon,
  Add as AddIcon
} from '@mui/icons-material';
import api from '../../api';
import { useApproval } from '../hooks';
import { useCategories, useNotification, useImageUpload } from '../../shared/hooks';
import { RejectionDialog } from '../components';
import TagSelector from '../../components/TagSelector';
import { AVAILABLE_LANGUAGES } from '../../utils/languages';

/**
 * SuggestionDetail page component
 * Full-screen page matching AddFoodForm layout
 */
const SuggestionDetail = ({
  type = 'food',
  basePath = '/suggestions'
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [suggestion, setSuggestion] = useState(null);
  const [formData, setFormData] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);

  // Hooks
  const { categories, loading: categoriesLoading } = useCategories();
  const { approve, reject, loading: actionLoading } = useApproval(type);
  const { snackbar, showNotification, hideNotification } = useNotification();
  const { upload: uploadImage, uploading: imageUploading } = useImageUpload();

  // Cuisine types from backend enum
  const cuisineTypes = [
    'ITALIAN', 'FRENCH', 'INDIAN', 'TURKISH', 'CHINESE', 'JAPANESE', 'MEXICAN',
    'GERMAN', 'SPANISH', 'GREEK', 'MEDITERRANEAN', 'MIDDLE_EASTERN', 'ASIAN',
    'EUROPEAN', 'AMERICAN', 'LATIN_AMERICAN', 'AFRICAN', 'CARIBBEAN',
    'SCANDINAVIAN', 'KOREAN', 'THAI', 'VIETNAMESE', 'LEBANESE', 'PERSIAN',
    'MOROCCAN', 'BRITISH', 'PORTUGUESE', 'BRAZILIAN', 'ARGENTINIAN', 'PERUVIAN'
  ];

  // Format cuisine type for display
  const formatCuisineType = (type) => {
    if (!type) return '';
    return type.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  // Fetch countries
  useEffect(() => {
    const fetchCountries = async () => {
      setLoadingCountries(true);
      try {
        const response = await api.get('/community/country', {
          params: { language: 'english' }
        });
        if (response.data && response.data.success) {
          setCountries(response.data.data || []);
        }
      } catch (error) {
        console.error('Error fetching countries:', error);
        showNotification('Failed to load countries', 'error');
      } finally {
        setLoadingCountries(false);
      }
    };
    fetchCountries();
  }, [showNotification]);

  // Fetch suggestion details
  useEffect(() => {
    const fetchSuggestion = async () => {
      setLoading(true);
      try {
        const endpoint = type === 'recipe' ? '/suggested-recipes' : '/suggested-foods';
        const response = await api.get(`${endpoint}/${id}`);

        if (response.data && response.data.success) {
          const suggestionData = response.data.data;
          setSuggestion(suggestionData);

          // Initialize form data
          const item = suggestionData.food || suggestionData.recipe;
          const langSpec = AVAILABLE_LANGUAGES.reduce((acc, lang) => {
            acc[lang] = {
              name: item.languageSpec?.[lang]?.name || '',
              description: item.languageSpec?.[lang]?.description || '',
              customImageUrl: item.languageSpec?.[lang]?.customImageUrl || ''
            };
            return acc;
          }, {});

          setFormData({
            name: item.name || '',
            description: item.description || '',
            categoryId: item.categoryId || '',
            category: item.category || '',
            isCustom: item.isCustom || false,
            showOriginalAmount: item.showOriginalAmount || false,
            calories: item.calories?.toString() || '',
            protein: item.protein?.toString() || '',
            fat: item.fat?.toString() || '',
            carbs: item.carbs?.toString() || '',
            glycemicIndex: item.glycemicIndex?.toString() || '',
            unitType: item.unitType || 'g',
            unitValue: item.unitValue?.toString() || '100',
            portionSize: item.portionSize?.toString() || '',
            image: item.image || '',
            languageSpec: langSpec,
            selectedTagIds: item.tags?.map(t => t.id) || [],
            cuisineType: item.cuisineType || '',
            selectedCountryIds: item.foodCountries?.map(fc => fc.countryId) || []
          });
        } else {
          throw new Error('Failed to fetch suggestion');
        }
      } catch (error) {
        console.error('Error fetching suggestion:', error);
        showNotification('Failed to load suggestion', 'error');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchSuggestion();
    }
  }, [id, type, showNotification]);

  // Handle input change
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle language specific change
  const handleLanguageChange = (language, field, value) => {
    setFormData(prev => ({
      ...prev,
      languageSpec: {
        ...prev.languageSpec,
        [language]: {
          ...prev.languageSpec[language],
          [field]: value
        }
      }
    }));
  };

  // Handle image upload
  const handleMainImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const url = await uploadImage(file);
    if (url) {
      handleInputChange('image', url);
      showNotification('Image uploaded successfully', 'success');
    } else {
      showNotification('Failed to upload image', 'error');
    }
  };

  // Handle language image upload
  const handleLanguageImageUpload = async (language, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const url = await uploadImage(file);
    if (url) {
      handleLanguageChange(language, 'customImageUrl', url);
      if (!formData.image) {
        handleInputChange('image', url);
      }
      showNotification(`${language} image uploaded successfully`, 'success');
    } else {
      showNotification(`Failed to upload ${language} image`, 'error');
    }
  };

  // Handle approve
  const handleApprove = async () => {
    // Find the selected category name
    const selectedCategory = formData.categoryId ?
      categories.find(cat => cat.id === parseInt(formData.categoryId)) : null;

    const approvalData = {
      name: formData.name,
      description: formData.description,
      category: selectedCategory ? selectedCategory.name : formData.category,
      categoryId: formData.categoryId ? parseInt(formData.categoryId) : null,
      image: formData.image,
      languageSpec: formData.languageSpec,
      calories: formData.calories ? parseFloat(formData.calories) : null,
      protein: formData.protein ? parseFloat(formData.protein) : null,
      fat: formData.fat ? parseFloat(formData.fat) : null,
      carbs: formData.carbs ? parseFloat(formData.carbs) : null,
      glycemicIndex: formData.glycemicIndex ? parseFloat(formData.glycemicIndex) : null,
      unitType: formData.unitType,
      unitValue: formData.unitValue ? parseFloat(formData.unitValue) : 100,
      portionSize: formData.portionSize ? parseFloat(formData.portionSize) : null,
      showOriginalAmount: formData.showOriginalAmount || false,
      cuisineType: formData.cuisineType || null,
      tagIds: formData.selectedTagIds || [],
      countryIds: formData.selectedCountryIds || []
    };

    const result = await approve(suggestion.id, approvalData);

    if (result.success) {
      showNotification('Suggestion approved successfully!', 'success');
      setTimeout(() => navigate(basePath), 1500);
    } else {
      showNotification(result.error || 'Failed to approve suggestion', 'error');
    }
  };

  // Handle reject
  const handleConfirmReject = async (reason) => {
    const result = await reject(suggestion.id, reason);

    if (result.success) {
      showNotification('Suggestion rejected', 'success');
      setRejectDialogOpen(false);
      setTimeout(() => navigate(basePath), 1500);
    } else {
      showNotification(result.error || 'Failed to reject suggestion', 'error');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get first initial for preview
  const getInitial = () => {
    return formData?.name ? formData.name.charAt(0).toUpperCase() : '?';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!suggestion || !formData) {
    return (
      <Alert severity="error">
        Suggestion not found or failed to load.
      </Alert>
    );
  }

  const isPending = suggestion.status === 'pending';

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', backgroundColor: '#f8fafc', py: 3 }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, sm: 3, md: 4 } }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          underline="hover"
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Home
        </Link>
        <Link
          component="button"
          underline="hover"
          onClick={() => navigate(basePath)}
          sx={{ cursor: 'pointer' }}
        >
          Suggestions
        </Link>
        <Typography color="text.primary">{formData.name || 'Detail'}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{
        mb: 4,
        pb: 3,
        borderBottom: '2px solid #e2e8f0'
      }}>
        <Typography variant="h3" sx={{
          fontWeight: 'bold',
          color: '#1e293b',
          mb: 1,
          fontSize: { xs: '1.8rem', md: '2.2rem' }
        }}>
          🔍 Review Suggestion
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Chip
            label={suggestion.status || 'pending'}
            color={
              suggestion.status === 'approved' ? 'success' :
              suggestion.status === 'rejected' ? 'error' : 'warning'
            }
            sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <UserIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {suggestion.user?.username || suggestion.user?.email || 'Unknown User'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DateIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {formatDate(suggestion.createdAt)}
            </Typography>
          </Box>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(basePath)}
            variant="outlined"
            size="small"
          >
            Back to List
          </Button>
        </Stack>
      </Box>

      {/* Main Content */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>

        {/* Food Preview Card */}
        <Paper elevation={3} sx={{ p: 3, backgroundColor: 'white', borderRadius: 3 }}>
          <Typography variant="h6" gutterBottom color="primary.main" sx={{ fontWeight: 'bold' }}>
            📱 Food Preview
          </Typography>

          <Box sx={{ textAlign: 'center', mb: 3 }}>
            {formData.image ? (
              <Box sx={{
                width: 150,
                height: 150,
                mx: 'auto',
                borderRadius: 3,
                overflow: 'hidden',
                border: '3px solid #e2e8f0'
              }}>
                <img
                  src={formData.image}
                  alt={formData.name || "Food preview"}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </Box>
            ) : (
              <Box sx={{
                width: 150,
                height: 150,
                mx: 'auto',
                borderRadius: 3,
                backgroundColor: '#3b82f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '4rem',
                fontWeight: 'bold',
                border: '3px solid #e2e8f0'
              }}>
                {getInitial()}
              </Box>
            )}
          </Box>

          <Typography variant="h5" sx={{
            fontWeight: 'bold',
            textAlign: 'center',
            color: '#1e293b',
            mb: 1
          }}>
            {formData.name || "Food Name"}
          </Typography>

          <Typography variant="body2" sx={{
            textAlign: 'center',
            color: '#64748b',
            mb: 1
          }}>
            {categories.find(cat => cat.id === parseInt(formData.categoryId))?.name || "Select Category"}
          </Typography>

          {/* Description Preview */}
          {formData.description && (
            <Typography variant="body2" sx={{
              textAlign: 'center',
              color: '#475569',
              mb: 2,
              px: 2,
              fontStyle: 'italic',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              lineHeight: 1.5
            }}>
              "{formData.description}"
            </Typography>
          )}

          {/* Quick Stats */}
          {(formData.calories || formData.protein || formData.carbs || formData.fat) && (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 1,
              mt: 2,
              p: 2,
              backgroundColor: '#f1f5f9',
              borderRadius: 2
            }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="primary.main">{formData.calories || "0"}</Typography>
                <Typography variant="caption" color="text.secondary">Calories</Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="primary.main">{formData.protein || "0"}g</Typography>
                <Typography variant="caption" color="text.secondary">Protein</Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="primary.main">{formData.carbs || "0"}g</Typography>
                <Typography variant="caption" color="text.secondary">Carbs</Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="primary.main">{formData.fat || "0"}g</Typography>
                <Typography variant="caption" color="text.secondary">Fat</Typography>
              </Box>
            </Box>
          )}
        </Paper>

        {/* Form Sections */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          
          {/* Basic Information Section */}
          <Paper elevation={3} sx={{ p: 3, backgroundColor: 'white', borderRadius: 3 }}>
            <Typography variant="h6" gutterBottom color="primary.main" sx={{ fontWeight: 'bold' }}>
              📝 Basic Information
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Food Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  disabled={!isPending}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.categoryId}
                    label="Category"
                    onChange={(e) => handleInputChange("categoryId", e.target.value)}
                    disabled={!isPending || categoriesLoading}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="">
                      <em>Select a category</em>
                    </MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Cuisine Type</InputLabel>
                  <Select
                    value={formData?.cuisineType || ''}
                    label="Cuisine Type"
                    onChange={(e) => handleInputChange("cuisineType", e.target.value)}
                    disabled={!isPending}
                    sx={{
                      borderRadius: 2,
                      minWidth: "200px"
                    }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {cuisineTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {formatCuisineType(type)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Countries</InputLabel>
                  <Select
                    multiple
                    value={formData?.selectedCountryIds || []}
                    onChange={(e) => handleInputChange("selectedCountryIds", e.target.value)}
                    input={<OutlinedInput label="Countries" />}
                    renderValue={(selected) => {
                      if (!selected || selected.length === 0) return 'None';
                      const selectedNames = countries
                        .filter(c => selected.includes(c.id))
                        .map(c => c.name);
                      return selectedNames.join(', ');
                    }}
                    disabled={!isPending || loadingCountries}
                    sx={{
                      borderRadius: 2,
                      minWidth: "200px"
                    }}
                  >
                    {countries.map((country) => (
                      <MenuItem key={country.id} value={country.id}>
                        <Checkbox checked={(formData?.selectedCountryIds || []).indexOf(country.id) > -1} />
                        <ListItemText primary={country.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Unit Type"
                  value={formData.unitType}
                  onChange={(e) => handleInputChange("unitType", e.target.value)}
                  disabled={!isPending}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Unit Value"
                  value={formData.unitValue}
                  onChange={(e) => handleInputChange("unitValue", e.target.value)}
                  disabled={!isPending}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              {(formData.unitType.toLowerCase().includes('piece') ||
                formData.unitType.toLowerCase().includes('portion')) && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Portion Size (grams)"
                    value={formData.portionSize}
                    onChange={(e) => handleInputChange("portionSize", e.target.value)}
                    disabled={!isPending}
                    helperText="Weight in grams of 1 piece/portion"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  value={formData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)}
                  disabled={!isPending}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid item xs={12}>
                <TagSelector
                  selectedTagIds={formData.selectedTagIds}
                  onTagsChange={(tagIds) => handleInputChange('selectedTagIds', tagIds)}
                  currentLanguage="en"
                  disabled={!isPending}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.showOriginalAmount || false}
                      onChange={(e) => handleInputChange("showOriginalAmount", e.target.checked)}
                      disabled={!isPending}
                      color="primary"
                    />
                  }
                  label="Show Original Amount"
                  sx={{
                    mt: 1,
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.875rem',
                      color: 'text.secondary'
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Image Upload Section */}
          <Paper elevation={3} sx={{ p: 3, backgroundColor: 'white', borderRadius: 3 }}>
            <Typography variant="h6" gutterBottom color="primary.main" sx={{ fontWeight: 'bold' }}>
              📷 Image Settings
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Image URL"
                  value={formData.image}
                  onChange={(e) => handleInputChange("image", e.target.value)}
                  disabled={!isPending}
                  placeholder="https://example.com/image.jpg"
                  helperText="Paste URL or use upload button below"
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': { borderRadius: 2 }
                  }}
                />

                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  disabled={!isPending || imageUploading}
                  sx={{
                    height: '56px',
                    borderRadius: 2,
                    borderStyle: 'dashed',
                    borderWidth: 2
                  }}
                >
                  {imageUploading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <AddIcon sx={{ mr: 1 }} />
                      Upload Food Image
                    </>
                  )}
                  <input
                    type="file"
                    hidden
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                    onChange={handleMainImageUpload}
                    disabled={!isPending}
                  />
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Nutritional Information Section */}
          <Paper elevation={3} sx={{ p: 3, backgroundColor: 'white', borderRadius: 3 }}>
            <Typography variant="h6" gutterBottom color="primary.main" sx={{ fontWeight: 'bold' }}>
              🥗 Nutritional Information
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Calories"
                  value={formData.calories}
                  onChange={(e) => handleInputChange("calories", e.target.value)}
                  disabled={!isPending}
                  InputProps={{ inputProps: { min: 0, step: 'any' } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Glycemic Index"
                  value={formData.glycemicIndex}
                  onChange={(e) => handleInputChange("glycemicIndex", e.target.value)}
                  disabled={!isPending}
                  InputProps={{ inputProps: { min: 0, max: 100, step: 'any' } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Protein (g)"
                  value={formData.protein}
                  onChange={(e) => handleInputChange("protein", e.target.value)}
                  disabled={!isPending}
                  InputProps={{ inputProps: { min: 0, step: 'any' } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Fat (g)"
                  value={formData.fat}
                  onChange={(e) => handleInputChange("fat", e.target.value)}
                  disabled={!isPending}
                  InputProps={{ inputProps: { min: 0, step: 'any' } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Carbs (g)"
                  value={formData.carbs}
                  onChange={(e) => handleInputChange("carbs", e.target.value)}
                  disabled={!isPending}
                  InputProps={{ inputProps: { min: 0, step: 'any' } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Language-Specific Content */}
          <Paper elevation={3} sx={{ p: 3, backgroundColor: 'white', borderRadius: 3 }}>
            <Typography variant="h6" gutterBottom color="primary.main" sx={{ fontWeight: 'bold' }}>
              🌐 Language-Specific Content
            </Typography>
            <Tabs
              value={currentTab}
              onChange={(e, newValue) => setCurrentTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
            >
              {AVAILABLE_LANGUAGES.map((lang) => (
                <Tab key={lang} label={lang.toUpperCase()} />
              ))}
            </Tabs>
            {AVAILABLE_LANGUAGES.map((lang, index) => (
              currentTab === index && (
                <Box key={lang} sx={{ mt: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={`Name (${lang})`}
                        value={formData.languageSpec[lang]?.name || ''}
                        onChange={(e) => handleLanguageChange(lang, 'name', e.target.value)}
                        disabled={!isPending}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label={`Description (${lang})`}
                        value={formData.languageSpec[lang]?.description || ''}
                        onChange={(e) => handleLanguageChange(lang, 'description', e.target.value)}
                        disabled={!isPending}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={`Custom Image URL (${lang})`}
                        value={formData.languageSpec[lang]?.customImageUrl || ''}
                        onChange={(e) => handleLanguageChange(lang, 'customImageUrl', e.target.value)}
                        disabled={!isPending}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, mb: 2 }}
                      />
                      <Button
                        variant="outlined"
                        component="label"
                        fullWidth
                        disabled={!isPending || imageUploading}
                        sx={{
                          height: '48px',
                          borderRadius: 2,
                          borderStyle: 'dashed',
                          borderWidth: 2
                        }}
                      >
                        {imageUploading ? (
                          <>
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <AddIcon sx={{ mr: 1 }} />
                            Upload {lang.toUpperCase()} Image
                          </>
                        )}
                        <input
                          type="file"
                          hidden
                          accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                          onChange={(e) => handleLanguageImageUpload(lang, e)}
                          disabled={!isPending}
                        />
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )
            ))}
          </Paper>
        </Box>

        {/* Action Buttons - At Bottom */}
        {isPending ? (
          <Paper elevation={3} sx={{ p: 3, backgroundColor: 'white', borderRadius: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<ApproveIcon />}
                onClick={handleApprove}
                disabled={actionLoading || !formData.name || !formData.categoryId}
                size="large"
                fullWidth
                sx={{
                  height: '56px',
                  fontWeight: 'bold'
                }}
              >
                {actionLoading ? "Approving..." : "Approve Suggestion"}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<RejectIcon />}
                onClick={() => setRejectDialogOpen(true)}
                disabled={actionLoading}
                size="large"
                fullWidth
                sx={{
                  height: '56px',
                  fontWeight: 'bold'
                }}
              >
                Reject Suggestion
              </Button>
            </Box>
          </Paper>
        ) : (
          <Alert severity={suggestion.status === 'approved' ? 'success' : 'error'}>
            This suggestion has been {suggestion.status}.
            {suggestion.rejectionReason && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" display="block">
                  Reason: {suggestion.rejectionReason}
                </Typography>
              </Box>
            )}
          </Alert>
        )}
      </Box>

      {/* Rejection Dialog */}
      <RejectionDialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onConfirm={handleConfirmReject}
        loading={actionLoading}
      />

      {/* Notification Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={hideNotification}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Box>
    </Box>
  );
};

export default SuggestionDetail;